import type { Meta } from './common'
import type { Image } from './media'
import type { FilterOption } from './shop'

export enum StockStatus {
  InStock = 'instock',
  OutOfStock = 'outofstock',
}

export enum ProductType {
  External = 'external',
  Grouped = 'grouped',
  Simple = 'simple',
  Variable = 'variable',
  Variation = 'variation',
}

export type Product =
  | ExternalProduct
  | GroupedProduct
  | SimpleProduct
  | VariableProduct
  | VariationProduct

export type ProductCommon = {
  id: number
  image: Image
  name: string
  price: number
  // priceHtml: string;
  regularPrice: number
  salePrice: number
  sku: string
  slug: string
  stockStatus: StockStatus
  type: ProductType
  url: string
}

export type SimpleProduct = {
  gallery: Image[]
  isNewArrival: boolean
  packshotImage: Image
  secondImage: Image
  sizeGuideImage: Image
  type: ProductType.Simple
} & ProductCommon

export type ExternalProduct = {
  buttonLabel: string
  buttonText: string
  type: ProductType.External
} & ProductCommon

export type GroupedProduct = {
  products: Product[]
  type: ProductType.Grouped
} & ProductCommon

export type VariableProduct = {
  gallery: Image[]
  isNewArrival: boolean
  options?: Option[]
  packshotImage: Image
  secondImage: Image
  sizeGuideImage: Image
  sizeText: string
  type: ProductType.Variable
  variations: VariationProduct[]
} & ProductCommon

export type VariationProduct = {
  gallery: Image[]
  secondImage: Image
  terms?: Terms
  type: ProductType.Variation
} & ProductCommon

export enum OptionName {
  Color = 'Color',
  Size = 'Size',
}

export enum OptionSlug {
  PaColor = 'pa_color',
  PaSize = 'pa_size',
}

export enum SwatchType {
  ColourSwatch = 'colour-swatch',
  DuoColourSwatch = 'duo-colour-swatch',
  ImageSwatch = 'image-swatch',
  TextSwatch = 'text-swatch',
}

export type Option = {
  name: OptionName
  slug: OptionSlug
  swatches: Swatch[]
  terms: string[]
}

export type Swatch = ColorSwatch | DuoColorSwatch | ImageSwatch | TextSwatch

export type SwatchCommon = {
  enableTooltip: boolean
  isAvailable: boolean
  label: string
  slug: string
}

export type ColorSwatch = {
  color: string
  type: SwatchType.ColourSwatch
} & SwatchCommon

export type DuoColorSwatch = {
  colors: string[]
  type: SwatchType.DuoColourSwatch
} & SwatchCommon

export type ImageSwatch = {
  imageUrl: string
  type: SwatchType.ImageSwatch
} & SwatchCommon

export type TextSwatch = {
  type: SwatchType.TextSwatch
} & SwatchCommon

export type Terms = { [key: string]: string }
export type PaColor =
  | 'black'
  | 'brown'
  | 'green'
  | 'navy-blue'
  | 'pink'
  | 'white'
export type PaSize = 'free-size' | 'l' | 'm' | 's' | 'xl' | 'xs'

export enum Sort {
  Date = 'date',
  Default = 'default',
  Popularity = 'popularity',
  Price = 'price',
  PriceDesc = 'price-desc',
}

export type ProductMetaAttributes = {
  categories: FilterOption[]
  colors: FilterOption[]
  sizes: FilterOption[]
}

export type ProductMetaTerm = {
  count: number
  description: string
  id: number
  name: string
  parent: number
  slug: string
  thumbnail?: Image
}

export type ProductDetails = {
  brands: string
  careInstruction?: CareInstruction
  collections: string[]
  completeTheLook: CompleteTheLook
  composition: string
  description: string
  fashionModels?: FashionModel[]
  information: string
  meta: Meta
  productId: string
  shortDescription: string
  upsellProducts: Product[]
} & Product

export type CompleteTheLook = {
  gallery: Image[]
  products: Product[]
}

export type CareInstruction = {
  icon: Image
  precautions: string
  title: string
}

export type FashionModel = {
  bust?: number
  height?: number
  hips?: number
  image: Image
  name: string
  size?: string
  waist?: number
}
